// src/pages/Dashboard.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { axiosClient } from "../core/api/axiosClient";
import { tokenStorage } from "../core/storage/tokenStorage";

const menuItems = [
  { label: "Reports", to: "/dashboard/reports" },
  { label: "Products", to: "/dashboard/products" },
  { label: "Sales", to: "/dashboard/sales" },
  { label: "Orders", to: "/dashboard/orders" },
  { label: "Leads", to: "/dashboard/leads" },
  { label: "Earnings", to: "/dashboard/earning" },
  { label: "Expenses", to: "/dashboard/expenses" },
  { label: "Contributions", to: "/dashboard/contributions" },
  { label: "Blogs", to: "/dashboard/blogs" },
  { label: "Demo Requests", to: "/dashboard/demorequests" },
  { label: "Contacts", to: "/dashboard/contacts" },
  { label: "Careers", to: "/dashboard/careers" },
  { label: "Employees", to: "/dashboard/employees" },
  { label: "Reviews", to: "/dashboard/reviews" },
  { label: "Profile", to: "/dashboard/profile" },
];

export default function DashboardLayout() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  const logout = useCallback(
    (message) => {
      tokenStorage.clear();
      navigate("/login", { replace: true, state: { message } });
    },
    [navigate]
  );

  useEffect(() => {
    let mounted = true;

    const loadUser = async () => {
      try {
        setLoadingUser(true);
        const res = await axiosClient.get("/users/me/");
        if (!mounted) return;
        setUser(res?.data || null);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401) {
          logout("Session expired, please login again.");
          return;
        }
        if (mounted) setUser(null);
      } finally {
        if (mounted) setLoadingUser(false);
      }
    };

    loadUser();
    return () => {
      mounted = false;
    };
  }, [logout]);

  const displayName = useMemo(() => {
    if (!user) return "";
    return user.first_name || user.name || user.username || user.email || "";
  }, [user]);

  const linkBase =
    "flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-all duration-200";

  const linkInactive =
    "text-secondary/80 hover:bg-primary/30 hover:text-secondary";
  const linkActive = "bg-secondary text-accent shadow-sm";

  return (
    <div className="min-h-screen bg-gradient-to-br from-accent via-primary/20 to-primary/10">
      {sidebarOpen ? (
        <button
          className="fixed inset-0 z-30 bg-black/30 md:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close sidebar overlay"
        />
      ) : null}

      <aside
        className={[
          // IMPORTANT FIX: flex flex-col so logout stays at bottom
          "fixed z-40 top-0 left-0 h-full w-72 flex flex-col",
          "bg-white/85 backdrop-blur-sm border-r border-secondary/30 shadow-lg",
          "transform transition-transform duration-300",
          sidebarOpen ? "translate-x-0" : "-translate-x-full",
          "md:translate-x-0",
        ].join(" ")}
      >
        <div className="h-16 flex items-center justify-between px-5 border-b border-secondary/20">
          <div className="flex items-center gap-2">
            <span className="h-9 w-9 rounded-xl bg-secondary text-accent grid place-items-center font-bold">
              MJ
            </span>
            <div>
              <div className="text-secondary font-bold leading-tight">
                MockingJay
              </div>
              <div className="text-xs text-secondary/70">Admin Panel</div>
            </div>
          </div>

          <button
            className="md:hidden rounded-lg border border-secondary/30 px-2 py-1 text-secondary"
            onClick={() => setSidebarOpen(false)}
          >
            ✕
          </button>
        </div>

        {/* nav scrollable + takes remaining height */}
        <nav className="p-4 space-y-1 flex-1 overflow-auto">
          {menuItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `${linkBase} ${isActive ? linkActive : linkInactive}`
              }
              onClick={() => setSidebarOpen(false)}
              end
            >
              <span className="h-2 w-2 rounded-full bg-secondary/60" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Footer section inside sidebar */}
        <div className="p-4 border-t border-secondary/20">
          <button
            onClick={() => logout()}
            className="w-full rounded-xl border border-secondary/40 px-4 py-2 text-secondary hover:bg-primary/30 transition"
          >
            Logout
          </button>
        </div>
      </aside>

      <div className="md:ml-72">
        <header className="sticky top-0 z-20 h-16 flex items-center justify-between px-4 sm:px-6 bg-white/70 backdrop-blur-sm border-b border-secondary/20">
          <div className="flex items-center gap-3">
            <button
              className="md:hidden rounded-xl border border-secondary/30 px-3 py-2 text-secondary"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open sidebar"
            >
              ☰
            </button>

            <div className="leading-tight">
              <div className="text-secondary font-semibold">
                {loadingUser
                  ? "Welcome"
                  : `Welcome${displayName ? `, ${displayName}` : ""}`}
              </div>
              <div className="text-xs text-secondary/70">
                Manage your modules from sidebar
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="hidden sm:inline-flex items-center gap-2 px-3 py-1 rounded-full border border-secondary/30 text-secondary text-sm bg-white/70">
              {user?.role ? String(user.role).toUpperCase() : "ADMIN"}
            </span>
          </div>
        </header>

        <main className="p-4 sm:p-6">
          <div className="rounded-2xl border border-secondary/20 bg-white/70 backdrop-blur-sm shadow-lg p-4 sm:p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
